import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Refresh, Search } from "@mui/icons-material";
import { alpha, styled } from "@mui/material/styles";
import AppPagination from "components/AppPagination";
import ConfirmationDialog from "components/ConfirmationDialog";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import buttonConfig from "config/ConfigAccessButton";
import { usePageTitle } from "contexts/PageTitleContext";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useCallback, useEffect, useState } from "react";
import TransactionTable from "./TransactionTable";
import {
  STATUS_TABS,
  extractPaginationFromResponse,
  extractPaymentsFromResponse,
  getDefaultDateRange,
  normalizePaymentRow,
  toApiDate,
} from "./transactionUtils";

const FilterCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
}));

const DatePickerField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius,
  },
}));

const pageSizeOptions = [10, 25, 50, 100];

const matchesPaymentSearch = (payment, term) => {
  const query = term.trim().toLowerCase();
  if (!query) return true;

  const searchableValues = [
    payment?.order_id,
    payment?.order_number,
    payment?.farmer_name,
    payment?.customer_name,
    payment?.transaction_id,
    payment?.transaction_reference_no,
    payment?.order?.farmer_name,
    payment?.order?.customer_name,
    payment?.order?.order_id,
    payment?.order?.order_number,
    payment?.raw?.order_id,
    payment?.raw?.farmer_name,
    payment?.raw?.customer_name,
  ];

  return searchableValues.some((value) =>
    String(value ?? "").toLowerCase().includes(query)
  );
};

const Transaction = () => {
  const { setPageTitle } = usePageTitle();
  const toast = useToast();
  const isCompanyUser = apiService.isCompanyUser();
  const companyId = apiService.getCompanyId();
  const companyDetails = apiService.getCompanyDetails();

  const defaultRange = getDefaultDateRange();
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState(defaultRange.fromDate);
  const [toDate, setToDate] = useState(defaultRange.toDate);
  const [searchValue, setSearchValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [updatingId, setUpdatingId] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(isCompanyUser ? companyId : "all");
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmButtonText: "Confirm",
    confirmButtonColor: "primary",
  });

  const getCurrentButtonPermissions = () => buttonConfig["Transaction"] || {};
  const shouldEnableButton = (buttonKey) => getCurrentButtonPermissions()[buttonKey] === true;

  const showError = useCallback(
    (message) => {
      toast.showError(message);
    },
    [toast]
  );

  const showSuccess = useCallback(
    (message) => {
      toast.showSuccess(message);
    },
    [toast]
  );

  const fetchCompanies = useCallback(async () => {
    try {
      if (isCompanyUser && companyDetails) {
        setCompanies([companyDetails]);
        return;
      }
      const res = await apiService.get(API_ENDPOINTS.GET_company);
      if (res?.success) {
        setCompanies(res.data?.Company || []);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  }, [companyDetails, isCompanyUser]);

  const fetchPayments = useCallback(async () => {
    setIsLoading(true);
    const userId = localStorage.getItem("user_id");
    const isSearchActive = Boolean(searchTerm.trim());
    const fetchPage = isSearchActive ? 1 : currentPage;
    const fetchPageSize = isSearchActive ? 10000 : pageSize;

    try {
      const params = new URLSearchParams();
      params.append("from_date", toApiDate(fromDate));
      params.append("to_date", toApiDate(toDate));
      params.append("user_id", userId || "");
      if (statusFilter && statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      params.append("page", String(fetchPage));
      params.append("page_size", String(fetchPageSize));
      if (selectedCompany && selectedCompany !== "all") {
        params.append("company", selectedCompany);
      }
      if (isSearchActive) {
        params.append("search", searchTerm.trim());
      }

      const response = await apiService.get(
        `${API_ENDPOINTS.GET_pending_payments}?${params.toString()}`
      );

      const rawRows = extractPaymentsFromResponse(response);
      let normalized = rawRows.map(normalizePaymentRow);

      if (isSearchActive) {
        normalized = normalized.filter((payment) => matchesPaymentSearch(payment, searchTerm));
        setPayments(normalized);
        setTotalPages(1);
        setTotalItems(normalized.length);
        return;
      }

      setPayments(normalized);

      const pagination = extractPaginationFromResponse(response);
      if (pagination) {
        setTotalPages(pagination.total_pages ?? 1);
        setTotalItems(pagination.total_items ?? normalized.length);
      } else {
        setTotalPages(1);
        setTotalItems(normalized.length);
      }
    } catch (error) {
      console.error("Error fetching pending payments:", error);
      const message =
        typeof error === "string"
          ? error
          : error?.message || error?.detail || "Failed to load transactions";
      showError(message);
      setPayments([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    fromDate,
    pageSize,
    searchTerm,
    selectedCompany,
    showError,
    statusFilter,
    toDate,
  ]);

  useEffect(() => {
    setPageTitle("Transaction");
    fetchCompanies();
  }, [fetchCompanies, setPageTitle]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleSearch = () => {
    const term = searchValue.trim();
    if (!term) return;
    setSearchTerm(term);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (searchValue === "" && searchTerm !== "") {
      setSearchTerm("");
      setCurrentPage(1);
    }
  }, [searchValue, searchTerm]);

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleUpdateStatus = async (payment, newStatus) => {
    const paymentId = payment?.id;
    if (!paymentId) {
      showError("Invalid transaction record.");
      return;
    }

    setUpdatingId(paymentId);
    try {
      const response = await apiService.put(API_ENDPOINTS.UPDATE_transaction_status, {
        id: paymentId,
        advance_payment_done_id: paymentId,
        status: newStatus,
        user_id: localStorage.getItem("user_id"),
      });

      if (response?.success === false) {
        throw new Error(response?.message || response?.error || "Failed to update transaction status");
      }

      showSuccess(
        response?.message ||
          `Transaction ${newStatus === "approved" ? "approved" : "rejected"} successfully.`
      );
      await fetchPayments();
    } catch (error) {
      console.error("Update transaction status error:", error);
      const message =
        typeof error === "string"
          ? error
          : error?.message || error?.detail || "Failed to update transaction status";
      showError(message);
    } finally {
      setUpdatingId(null);
    }
  };

  const showStatusConfirmation = (payment, action) => {
    const isApprove = action === "approved";

    setConfirmationDialog({
      open: true,
      title: isApprove ? "Accept Transaction" : "Reject Transaction",
      message: isApprove
        ? "Are you sure you want to accept this order? Once the status is updated, it cannot be changed again."
        : "Are you sure you want to reject this order? Once the status is updated, it cannot be changed again.",
      confirmButtonText: isApprove ? "Accept" : "Reject",
      confirmButtonColor: isApprove ? "success" : "error",
      onConfirm: async () => {
        setConfirmationDialog((prev) => ({ ...prev, open: false }));
        await handleUpdateStatus(payment, action);
      },
    });
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <FilterCard>
        <Box sx={{ p: 1.5 }}>
          <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
              <FlexBox gap={1}>
                <SearchInput
                  fullWidth
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Search transactions..."
                />
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleSearch}
                  disabled={!searchValue.trim()}
                  sx={{ textTransform: "none", minWidth: 96, height: 40 }}
                >
                  Search
                </Button>
              </FlexBox>
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {STATUS_TABS.map((tab) => (
                    <MenuItem key={tab.value} value={tab.value}>
                      {tab.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.9}>
              <DatePickerField
                fullWidth
                size="small"
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setCurrentPage(1);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: toDate || undefined }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.9}>
              <DatePickerField
                fullWidth
                size="small"
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setCurrentPage(1);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: fromDate || undefined, max: new Date().toISOString().split("T")[0] }}
              />
            </Grid>
            {/* <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Company</InputLabel>
                <Select
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  label="Company"
                  disabled={isCompanyUser}
                >
                  {!isCompanyUser && (
                    <MenuItem value="all">
                      <Chip label="All" size="small" sx={{ mr: 1 }} />
                      All Companies
                    </MenuItem>
                  )}
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}
            
            <Grid item xs={12} sm={6} md={1}>
              <FormControl fullWidth size="small">
                <InputLabel>Rows</InputLabel>
                <Select
                  value={pageSize}
                  label="Rows"
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {pageSizeOptions.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={fetchPayments}
                disabled={isLoading}
                sx={{ textTransform: "none", height: 40, width: "100%" }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Box>
      </FilterCard>

      <TransactionTable
        payments={payments}
        isLoading={isLoading}
        statusFilter={statusFilter}
        updatingId={updatingId}
        canApprove={shouldEnableButton("accepted")}
        canReject={shouldEnableButton("rejected")}
        onApprove={(payment) => showStatusConfirmation(payment, "approved")}
        onReject={(payment) => showStatusConfirmation(payment, "rejected")}
      />

      {!isLoading && totalPages > 0 && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {totalItems > 0
              ? `Showing ${(currentPage - 1) * pageSize + 1} - ${Math.min(currentPage * pageSize, totalItems)} of ${totalItems} transactions`
              : "Showing 0 of 0 transactions"}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <AppPagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
          </Box>
        </Box>
      )}

      <ConfirmationDialog
        open={confirmationDialog.open}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmButtonText={confirmationDialog.confirmButtonText}
        confirmButtonColor={confirmationDialog.confirmButtonColor}
        onConfirm={confirmationDialog.onConfirm}
        onClose={() => setConfirmationDialog((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default Transaction;
